'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { WebPushConfig } from '@/lib/emarsys/web-push-config';
import {
  initializeWebPush,
  subscribeToWebPush,
  ownsWebPush,
  publishWebPushConsent,
  hasSharedWebPushConsent,
  WEB_PUSH_CONSENT_KEY,
  withPushTimeout,
  getWebPushSubscription,
  supportsWebPush,
  withdrawWebPushConsent,
} from '@/lib/emarsys/web-push';

interface PushState {
  config: WebPushConfig | null;
  subscribed: boolean;
  consent: boolean;
  busy: boolean;
  blocked: boolean;
  error: boolean;
  supported: boolean;
  toggle: () => void;
}

const WebPushContext = createContext<PushState | null>(null);

export function WebPushProvider({
  config,
  children,
}: {
  config: WebPushConfig | null;
  children: React.ReactNode;
}) {
  const [subscribed, setSubscribed] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [error, setError] = useState(false);
  const [supported, setSupported] = useState(false);
  const generation = useRef(0);
  const mutation = useRef(false);

  useEffect(() => {
    if (
      !config ||
      !supportsWebPush() ||
      window.location.origin !== config.origin
    )
      return;
    setSupported(true);
    let disposed = false;
    const invalidate = () => ++generation.current;
    const sync = (event?: Event) => {
      if (event?.type === 'focus' && mutation.current) return;
      if (event instanceof StorageEvent && event.key !== WEB_PUSH_CONSENT_KEY)
        return;
      const currentRevision = invalidate();
      setBusy(false);
      const operation = async () => {
        const allowed = hasSharedWebPushConsent(config);
        setConsent(allowed);
        if (!allowed) {
          setSubscribed(false);
          if (ownsWebPush(config))
            await withPushTimeout(withdrawWebPushConsent(config));
          return;
        }
        if (Notification.permission !== 'granted') {
          setBlocked(Notification.permission === 'denied');
          return;
        }
        setBusy(true);
        await withPushTimeout(initializeWebPush(config));
        const current = await withPushTimeout(getWebPushSubscription(config));
        if (
          disposed ||
          generation.current !== currentRevision ||
          !hasSharedWebPushConsent(config)
        )
          return;
        setSubscribed(current);
        setBlocked(false);
        setError(false);
        setBusy(false);
      };
      void operation().catch(() => {
        if (!disposed && generation.current === currentRevision) {
          setBusy(false);
          setError(true);
          console.error('[WebPush] Initialization or consent cleanup failed');
        }
      });
    };
    sync();
    window.addEventListener('focus', sync);
    window.addEventListener('storage', sync);
    return () => {
      disposed = true;
      invalidate();
      window.removeEventListener('focus', sync);
      window.removeEventListener('storage', sync);
    };
  }, [config]);

  const toggle = () => {
    if (!config || mutation.current || busy || !supported) return;
    setBusy(true);
    mutation.current = true;
    setError(false);
    const currentGeneration = generation.current;
    const current = () => generation.current === currentGeneration;
    let operation: Promise<void>;
    try {
      if (consent || subscribed) {
        publishWebPushConsent(config, false);
        setConsent(false);
        setSubscribed(false);
        operation = withdrawWebPushConsent(config);
      } else {
        operation = subscribeToWebPush(config);
        setConsent(hasSharedWebPushConsent(config));
      }
    } catch {
      setError(true);
      setBusy(false);
      mutation.current = false;
      return;
    }
    void withPushTimeout(operation)
      .then(async () => {
        if (!hasSharedWebPushConsent(config)) {
          await withPushTimeout(withdrawWebPushConsent(config));
          if (current()) {
            setConsent(false);
            setSubscribed(false);
            setBlocked(Notification.permission === 'denied');
          }
          return;
        }
        const isSubscribed = await withPushTimeout(
          getWebPushSubscription(config),
        );
        if (!current() || !hasSharedWebPushConsent(config)) return;
        setConsent(true);
        setSubscribed(isSubscribed);
        setBlocked(Notification.permission === 'denied');
        if (
          isSubscribed === subscribed &&
          Notification.permission === 'granted'
        )
          setError(true);
      })
      .catch(() => {
        if (current()) {
          setConsent(hasSharedWebPushConsent(config));
          setError(true);
        }
      })
      .finally(() => {
        mutation.current = false;
        if (current()) setBusy(false);
      });
  };

  return (
    <WebPushContext.Provider
      value={{
        config,
        subscribed,
        consent,
        busy,
        blocked,
        error,
        supported,
        toggle,
      }}
    >
      {children}
    </WebPushContext.Provider>
  );
}

export function useWebPushState(): PushState | null {
  return useContext(WebPushContext);
}

export function WebPushControl() {
  const state = useContext(WebPushContext);
  if (!state?.config || !state.supported) return null;
  const { config, subscribed, consent, busy, blocked, error, toggle } = state;
  return (
    <div className="my-6 text-bodym">
      <label className="inline-flex max-w-full items-center gap-3">
        <input
          type="checkbox"
          checked={consent || subscribed}
          disabled={busy || (blocked && !consent && !subscribed)}
          onChange={toggle}
          className="h-5 w-5 shrink-0 accent-current"
        />
        <span className="break-words">
          {subscribed ? config.unsubscribeLabel : config.subscribeLabel}
        </span>
      </label>
      {blocked && <p role="status">{config.blockedLabel}</p>}
      {error && <p role="alert">{config.errorLabel}</p>}
    </div>
  );
}

